if (!customElements.get('sticky-product-cta')) {
  customElements.define(
    'sticky-product-cta',
    class StickyProductCta extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.primaryButton = this.productInfo?.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        this.variantText = this.querySelector('.sticky-product-cta__variant');
        this.priceText = this.querySelector('.sticky-product-cta__price');
        this.button = this.querySelector('.sticky-product-cta__button');
        if (!this.primaryButton || !this.button) return;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleOptionChange = this.handleOptionChange.bind(this);

        this.button.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleScroll, { passive: true });

        if (this.primaryButton instanceof Node) {
          this.primaryButtonObserver = new MutationObserver(() => this.syncButtonState());
          this.primaryButtonObserver.observe(this.primaryButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled'],
            childList: true,
            subtree: true,
          });
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionChange);

        this.syncButtonState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
        this.primaryButtonObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.optionChangeUnsubscriber?.();
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      }

      handleScroll() {
        if (this.animationFrame) return;

        this.animationFrame = requestAnimationFrame(() => {
          this.animationFrame = null;
          this.updateVisibility();
        });
      }

      updateVisibility() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const buttonBounds = this.primaryButton.getBoundingClientRect();
        const isPrimaryButtonVisible = buttonBounds.top < window.innerHeight && buttonBounds.bottom > 0;
        const isVisible = isMobile && !isPrimaryButtonVisible;

        this.classList.toggle('is-visible', isVisible);
        this.setAttribute('aria-hidden', String(!isVisible));
        this.button.tabIndex = isVisible ? 0 : -1;
        if (!isVisible && document.activeElement === this.button) this.button.blur();
      }

      handleClick() {
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
        this.primaryButton.click();
      }

      handleOptionChange({ data: { event } }) {
        if (!this.productInfo.contains(event.target)) return;
        this.updateVariantTextFromInputs();
      }

      handleVariantChange({ data: { sectionId, html, variant } }) {
        if (String(sectionId) !== this.dataset.section || !variant) return;

        this.variantText.textContent = variant.title === 'Default Title' ? this.dataset.productTitle : variant.title;
        const priceSource = html.getElementById(`price-${sectionId}`);
        const selectedPrice = priceSource?.querySelector(
          '.price-item--sale.price-item--last, .price__regular .price-item--regular'
        );
        if (selectedPrice) this.priceText.textContent = selectedPrice.textContent.trim();
        this.syncButtonState();
      }

      updateVariantTextFromInputs() {
        const selectedValues = Array.from(this.productInfo.querySelectorAll('variant-selects .product-form__input'))
          .map((option) => {
            const checkedInput = option.querySelector('input[type="radio"]:checked');
            if (checkedInput) return checkedInput.value;

            const select = option.querySelector('select');
            return select?.options[select.selectedIndex]?.text.trim();
          })
          .filter(Boolean);

        if (selectedValues.length) this.variantText.textContent = selectedValues.join(' / ');
      }

      syncButtonState() {
        const primaryLabel = this.primaryButton.querySelector('span')?.textContent.trim();
        const isLoading = this.primaryButton.classList.contains('loading');
        const isDisabled = this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true';

        if (primaryLabel) this.button.querySelector('span').textContent = primaryLabel;
        this.button.disabled = isDisabled;
        this.button.setAttribute('aria-disabled', String(isDisabled));
        this.button.classList.toggle('loading', isLoading);
        this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
